import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { addUserToFacility as addUserToFacilityAction } from "./customersSlice"
import { RootState } from "."
import { ApiError, ApiResponse, Header } from "@/api"
import {
   requestUsers,
   requestCreateUser,
   UsersResponse,
   requestUpdateUser,
   requestDeleteUser,
} from "@/api/UsersService"

import { UserRequest } from "@/api/UsersService"
import { HttpCodes } from "@/constants/globals"
import { UserData } from "@/pages/Settings/Users/types"
import { User } from "@/types/user"
import { merge } from "@/utils/lodash"
import { formatUserData } from "@/utils/users"

interface UsersState {
   users: User[]
   loading: boolean
   loadingCreateUser: boolean
   error: number | null
   errorCreateUser: number | null
   loadingUpdateUser: boolean
   errorUpdateUser: number | null
   userIdToEdit: string | null
   loadingDeleteUser: boolean
   errorDeleteUser: number | null
   userIdToDelete: string | null
   loadingReinviteUser: boolean
   errorReinviteUser: number | null
}

export const initialState: UsersState = {
   users: [],
   loading: false,
   loadingCreateUser: false,
   error: null,
   errorCreateUser: null,
   loadingUpdateUser: false,
   errorUpdateUser: null,
   userIdToEdit: null,
   loadingDeleteUser: false,
   errorDeleteUser: null,
   userIdToDelete: null,
   loadingReinviteUser: false,
   errorReinviteUser: null,
}

export const fetchUsers = createAsyncThunk(
   "users/fetchUsers",
   async (header: Header, thunkAPI) => {
      try {
         const response: ApiResponse<UsersResponse[]> =
            await requestUsers(header)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const createUser = createAsyncThunk(
   "users/createUser",
   async (
      {
         header,
         userData,
         headerId,
      }: { header: Header; userData: UserRequest; headerId?: string },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<UsersResponse> = await requestCreateUser(
            header,
            userData,
            headerId
         )
         if (
            response.status === HttpCodes.CREATED &&
            header === Header.FACILITY_ID &&
            headerId
         ) {
            const state = thunkAPI.getState() as RootState
            const customers = state.customersReducer.customers
            const customer = customers.find((customer) =>
               customer.facilities.some((f) => f.id === headerId)
            )

            thunkAPI.dispatch(
               addUserToFacilityAction({
                  customerId: customer?.id,
                  facilityId: headerId,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateUser = createAsyncThunk(
   "users/updateUser",
   async (
      {
         header,
         userId,
         userData,
      }: { header: Header; userId: string; userData: UserRequest },
      thunkAPI
   ) => {
      try {
         thunkAPI.dispatch(setUserIdToEdit(userId))
         const response: ApiResponse<UsersResponse> = await requestUpdateUser(
            header,
            userId,
            userData
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const deleteUser = createAsyncThunk(
   "users/deleteUser",
   async ({ header, userId }: { header: Header; userId: string }, thunkAPI) => {
      thunkAPI.dispatch(setUserIdToDelete(userId))
      try {
         const response: ApiResponse<void> = await requestDeleteUser(
            header,
            userId
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const reinviteUser = createAsyncThunk(
   "users/reinviteUser",
   async ({ header, user }: { header: Header; user: UserData }, thunkAPI) => {
      try {
         const response: ApiResponse<UsersResponse> = await requestCreateUser(
            header,
            { ...formatUserData(user), newToken: true }
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const usersSlice = createSlice({
   name: "users",
   initialState,
   reducers: {
      resetCreateUserState: (state) => {
         state.loadingCreateUser = false
         state.errorCreateUser = null
      },
      setUserIdToEdit: (state, action: PayloadAction<string | null>) => {
         state.userIdToEdit = action.payload
      },
      setUserIdToDelete: (state, action: PayloadAction<string>) => {
         state.userIdToDelete = action.payload
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchUsers.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchUsers.fulfilled,
            (state, action: PayloadAction<ApiResponse<UsersResponse[]>>) => {
               state.loading = false
               state.users = action.payload.data
            }
         )
         .addCase(fetchUsers.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(createUser.pending, (state) => {
            state.loadingCreateUser = true
            state.errorCreateUser = null
         })
         .addCase(
            createUser.fulfilled,
            (state, action: PayloadAction<ApiResponse<UsersResponse>>) => {
               state.loadingCreateUser = false
               state.errorCreateUser = null
               state.users.push(action.payload.data)
            }
         )
         .addCase(createUser.rejected, (state, action) => {
            state.loadingCreateUser = false
            state.errorCreateUser = action.payload as number
         })
         .addCase(updateUser.pending, (state) => {
            state.loadingUpdateUser = true
            state.errorUpdateUser = null
         })
         .addCase(
            updateUser.fulfilled,
            (
               state,
               action: PayloadAction<
                  ApiResponse<UsersResponse>,
                  string,
                  { arg: { userId: string; userData: UserRequest } }
               >
            ) => {
               const { userId } = action.meta.arg
               const updatedUser = action.payload.data
               const index = state.users.findIndex((user) => user.id === userId)
               if (index !== -1) {
                  state.users[index] = merge(
                     {},
                     state.users[index],
                     updatedUser
                  )
               }

               state.loadingUpdateUser = false
               state.errorUpdateUser = null
            }
         )
         .addCase(updateUser.rejected, (state, action) => {
            state.loadingUpdateUser = false
            state.errorUpdateUser = action.payload as number
         })
         .addCase(deleteUser.pending, (state) => {
            state.loadingDeleteUser = true
            state.errorDeleteUser = null
         })
         .addCase(deleteUser.fulfilled, (state, action) => {
            const userId = state.users.findIndex(
               (user) => user.id === action.meta.arg.userId
            )
            if (userId !== -1) {
               state.users.splice(userId, 1)
            }
            state.loadingDeleteUser = false
            state.errorDeleteUser = null
         })
         .addCase(deleteUser.rejected, (state, action) => {
            state.loadingDeleteUser = false
            state.errorDeleteUser = action.payload as number
         })
         .addCase(reinviteUser.pending, (state) => {
            state.loadingReinviteUser = true
            state.errorReinviteUser = null
         })
         .addCase(reinviteUser.fulfilled, (state, action) => {
            const user = state.users.find(
               (user) => user.id === action.meta.arg.user.id
            )
            if (user) {
               user.status = action.payload.data.status
            }
            state.loadingReinviteUser = false
         })
         .addCase(reinviteUser.rejected, (state, action) => {
            state.loadingReinviteUser = false
            state.errorReinviteUser = action.payload as number
         })
   },
})

export const { resetCreateUserState, setUserIdToEdit, setUserIdToDelete } =
   usersSlice.actions

export default usersSlice.reducer
